import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HubLayout from '../../../components/hub/HubLayout.jsx';
import config from '../../../data/hubs/comptia-secplus/config.js';

const STORAGE_KEY = `${config.storagePrefix}.week.progress`;

const WEEKS = [
  { n: 1,  focus: 'Exam overview + General Security Concepts (D1)',          res: 'SY0-701 objectives PDF, Professor Messer intro videos',  goal: 'Memorize control types, CIA, Zero Trust planes' },
  { n: 2,  focus: 'Cryptography fundamentals (D1)',                          res: 'Chapple/Seidl Ch. 7, Messer crypto playlist',           goal: 'Explain symmetric vs. asymmetric, hashing, PKI flow' },
  { n: 3,  focus: 'Threats & Vectors (D2 part 1)',                           res: 'Chapple/Seidl Ch. 14, Dion attack labs',                goal: 'Memorize top 20 attack types and detection signals' },
  { n: 4,  focus: 'Vulnerabilities & Mitigations (D2 part 2)',               res: 'OWASP Top 10, ExamCompass D2 quiz',                     goal: 'Pair every attack with at least one mitigation' },
  { n: 5,  focus: 'Network Architecture (D3 part 1)',                        res: 'Chapple/Seidl Ch. 11, GNS3 firewall lab',               goal: 'Configure stateful FW rules; explain DMZ vs. screened subnet' },
  { n: 6,  focus: 'Cloud, Resilience & Data Protection (D3 part 2)',         res: 'AWS shared responsibility, Messer cloud videos',        goal: 'Diagram a hybrid cloud with proper data classification' },
  { n: 7,  focus: 'Identity & Access (D4 part 1)',                           res: 'Chapple/Seidl Ch. 8, Quizlet IAM decks',                goal: 'Distinguish RADIUS/TACACS+, OAuth, SAML, OIDC' },
  { n: 8,  focus: 'Operations: Hardening + Vuln Mgmt (D4 part 2)',           res: 'CIS Benchmarks, TryHackMe vuln rooms',                  goal: 'Run a full vuln scan and prioritize by CVSS' },
  { n: 9,  focus: 'Incident Response & Forensics (D4 part 3)',               res: 'SANS IR cycle, NIST 800-61',                            goal: 'Write a one-page ransomware IR playbook' },
  { n: 10, focus: 'Governance, Risk & Compliance (D5)',                      res: 'Chapple/Seidl Ch. 19, NIST CSF',                        goal: 'Compute SLE/ALE for a sample asset; map a risk register' },
  { n: 11, focus: 'Full-length practice exam #1',                            res: 'Boson ExSim or CompTIA CertMaster Practice',            goal: 'Take ONE timed exam; review every wrong answer in detail' },
  { n: 12, focus: 'Weak-spot review + cheat-sheet sprint',                   res: 'Cheat sheet, flashcards, Messer trouble-spot videos',   goal: 'Final sprint: 30-question rapid drills until exam day' },
];

function loadProgress() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}

export default function StudyPlan() {
  const [progress, setProgress] = useState(loadProgress);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(progress)); } catch { /* ignore */ }
  }, [progress]);

  const completed = WEEKS.filter((w) => progress[w.n]).length;
  const pct = Math.round((completed / WEEKS.length) * 100);

  function toggle(weekNum) {
    setProgress((p) => ({ ...p, [weekNum]: !p[weekNum] }));
  }
  function reset() {
    if (!confirm('Reset all weekly progress?')) return;
    setProgress({});
  }
  async function copyPlan() {
    const lines = [['Week', 'Focus', 'Primary Resources', 'Goal / Milestone'].join('\t')];
    WEEKS.forEach((w) => lines.push([String(w.n).padStart(2, '0'), w.focus, w.res, w.goal].join('\t')));
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }

  return (
    <HubLayout config={config}>
      <section>
        <div className="section-head">
          <h2>Mission Brief</h2>
          <span className="section-tag">briefing</span>
        </div>
        <p>
          Allocate <strong>1–2 hours per day, six days a week</strong> — about 10–12 hours total. The cadence is
          built around the {config.code} domain weighting, so heavier domains get more weeks. Check off each week as
          you complete it; progress saves to your browser locally.
        </p>
        <div className="callout">
          <strong>Tactical Note</strong>
          Do not skip Week 11. A timed full-length practice exam under realistic conditions is the single best
          predictor of pass/fail. Take it cold, then review every wrong answer before moving to Week 12.
        </div>
      </section>

      <section>
        <div className="section-head">
          <h2>Weekly Schedule</h2>
          <span className="section-tag">12-week tempo</span>
        </div>

        <div className="progress-block">
          <span className="label">Progress</span>
          <span className="progress-bar" style={{ '--pct': `${pct}%` }}></span>
          <span className="value">{completed}/{WEEKS.length} weeks · {pct}%</span>
          <button type="button" className="btn-mini no-print" onClick={reset}>Reset</button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Week</th>
                <th>Focus</th>
                <th>Primary Resources</th>
                <th>Goal / Milestone</th>
              </tr>
            </thead>
            <tbody>
              {WEEKS.map((w) => (
                <tr key={w.n} data-week={w.n} className={progress[w.n] ? 'week-done' : ''}>
                  <td className="check-cell">
                    <input
                      type="checkbox"
                      aria-label={`Mark week ${w.n} complete`}
                      checked={!!progress[w.n]}
                      onChange={() => toggle(w.n)}
                    />
                  </td>
                  <td>{String(w.n).padStart(2, '0')}</td>
                  <td>{w.focus}</td>
                  <td>{w.res}</td>
                  <td>{w.goal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="hero-cta" style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn no-print" onClick={copyPlan}>
            {copied ? '✓ Copied' : 'Copy plan to clipboard'}
          </button>
          <Link to={`/hub/${config.slug}/resources`} className="btn btn-ghost">Open resources module</Link>
        </div>
      </section>
    </HubLayout>
  );
}
